import {ReactElement, ReactNode, RefObject, useEffect, useRef} from 'react'
import {Trans, useTranslation} from 'react-i18next'

import {ChevronRightIcon, RenderIf} from 'components'
import {DopamineAssistantGuideIcon} from './DopamineAssistantGuideIcon'

const DOPAMINE_GUIDE_ID = 'dopamine-assistant-guide'
const GUIDE_TITLE_ID = 'dopamine-assistant-guide-title'
const GUIDE_TOPICS_ID = 'dopamine-assistant-guide-topics'

const GUIDE_TOPIC_KEYS = [
  'DOPAMINE_GUIDE_TOPIC_AI_APPS',
  'DOPAMINE_GUIDE_TOPIC_SENSITIVE_DATA',
  'DOPAMINE_GUIDE_TOPIC_USERS',
  'DOPAMINE_GUIDE_TOPIC_POLICIES',
  'DOPAMINE_GUIDE_TOPIC_SHADOW_IT',
] as const

type DopamineAssistantGuideToggleProps = {
  buttonRef: RefObject<HTMLButtonElement>
  isOpen: boolean
  onToggle(): void
}

// The header control, styled and placed like the other header icon buttons.
export const DopamineAssistantGuideToggle = ({
  buttonRef,
  isOpen,
  onToggle,
}: DopamineAssistantGuideToggleProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})

  return (
    <button
      aria-controls={isOpen ? DOPAMINE_GUIDE_ID : undefined}
      aria-label={t('DOPAMINE_GUIDE')}
      aria-pressed={isOpen}
      className="dopamine-header-icon-button dopamine-tip"
      data-cy="dopamine-assistant-guide-toggle"
      data-tip={t('DOPAMINE_GUIDE')}
      ref={buttonRef}
      type="button"
      onClick={onToggle}
    >
      <DopamineAssistantGuideIcon />
    </button>
  )
}

type DopamineAssistantGuideProps = {
  // Without a composer to fill (a read-only or loading chat), the example
  // question reads as plain text.
  canTryExample: boolean
  onBack(): void
  onTryExample(question: string): void
}

export const DopamineAssistantGuide = ({
  canTryExample,
  onBack,
  onTryExample,
}: DopamineAssistantGuideProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const backButtonRef = useRef<HTMLButtonElement>(null)
  const example = t('DOPAMINE_GUIDE_EXAMPLE')
  const exampleQuote = t('DOPAMINE_GUIDE_EXAMPLE_QUOTE', {question: example})

  // The guide only mounts when it is opened, so mounting is the hand-off.
  useEffect(() => {
    backButtonRef.current?.focus()
  }, [])

  const steps: {description: ReactNode; titleKey: string}[] = [
    {
      description: (
        <>
          {t('DOPAMINE_GUIDE_STEP_ASK_PREFIX')}{' '}
          {canTryExample ? (
            <button
              className="inline text-left text-dopamine-accent underline decoration-dopamine-accent/50 decoration-dashed underline-offset-4 transition-colors duration-base hover:text-white hover:decoration-solid focus:outline-none focus-visible:ring-1 focus-visible:ring-dopamine-accent motion-reduce:transition-none"
              data-cy="dopamine-assistant-guide-example"
              title={t('DOPAMINE_GUIDE_EXAMPLE_HINT')}
              type="button"
              onClick={() => onTryExample(example)}
            >
              {exampleQuote}
            </button>
          ) : (
            exampleQuote
          )}
        </>
      ),
      titleKey: 'DOPAMINE_GUIDE_STEP_ASK_TITLE',
    },
    {
      description: t('DOPAMINE_GUIDE_STEP_QUERY_DESCRIPTION'),
      titleKey: 'DOPAMINE_GUIDE_STEP_QUERY_TITLE',
    },
    {
      description: t('DOPAMINE_GUIDE_STEP_ANSWER_DESCRIPTION'),
      titleKey: 'DOPAMINE_GUIDE_STEP_ANSWER_TITLE',
    },
  ]

  return (
    <section
      aria-labelledby={GUIDE_TITLE_ID}
      data-cy="dopamine-assistant-guide"
      id={DOPAMINE_GUIDE_ID}
    >
      <div className="flex items-center gap-2 px-3 pb-2 pt-1">
        <button
          aria-label={t('DOPAMINE_GUIDE_BACK')}
          className="dopamine-header-icon-button"
          data-cy="dopamine-assistant-guide-back"
          ref={backButtonRef}
          type="button"
          onClick={onBack}
        >
          {/* Rotated on a wrapper: the hover scale on the svg would replace
              a rotation set on the svg itself and flip the arrow. */}
          <span aria-hidden="true" className="flex rotate-180">
            <ChevronRightIcon
              height={12}
              strokeColor="currentColor"
              strokeWidth={1.5}
              width={7}
            />
          </span>
        </button>
        <h3 className="text-sm font-bold text-white" id={GUIDE_TITLE_ID}>
          {t('DOPAMINE_GUIDE')}
        </h3>
      </div>
      {/* Enters like a chat message: same keyframes, same timing. */}
      <div className="animate-[dopamine-message-enter_0.35s_ease_both] px-5 pb-7 pt-2.5 motion-reduce:animate-none">
        <p className="abc-light mb-6 text-lg leading-snug text-white text-balance">
          <Trans
            components={[
              <span className="text-dopamine-accent" key="dope-security" />,
            ]}
            i18nKey="DOPAMINE_GUIDE_INTRO"
            t={t}
          />
        </p>
        <h4
          className="mb-2.5 text-11px uppercase tracking-widest text-dopamine-muted"
          id={GUIDE_TOPICS_ID}
        >
          {t('DOPAMINE_GUIDE_TOPICS')}
        </h4>
        <ul
          aria-labelledby={GUIDE_TOPICS_ID}
          className="mb-7 flex flex-wrap gap-1.5"
        >
          {GUIDE_TOPIC_KEYS.map(topicKey => (
            <li
              className="rounded-ds-pill border-0.5 border-white/15 bg-white/3 px-3 py-1.5 text-xs leading-snug text-white/85"
              key={topicKey}
            >
              {t(topicKey)}
            </li>
          ))}
        </ul>
        <ol aria-label={t('DOPAMINE_GUIDE_STEPS')} className="flex flex-col">
          {steps.map((step, index) => (
            <li
              className="relative flex gap-3.5 pb-5 last:pb-0"
              key={step.titleKey}
            >
              {/* The line runs from this step's number down to the next. */}
              <RenderIf isTrue={index < steps.length - 1}>
                <span
                  aria-hidden="true"
                  className="absolute bottom-1 left-3.5 top-9 w-px bg-dopamine-accent/20"
                />
              </RenderIf>
              <span
                aria-hidden="true"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-0.5 border-dopamine-accent/40 bg-dopamine-accent/10 text-xs font-bold text-dopamine-accent"
              >
                {index + 1}
              </span>
              <div className="min-w-0 pt-1">
                <p className="text-sm text-white">{t(step.titleKey)}</p>
                <p className="mt-1 text-xs leading-5 text-dopamine-muted">
                  {step.description}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}
